import React, { useState } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { ImprovementPlanItem } from '../../models/improvementPlan';

const formatCurrency = (value: number): string => `$${value.toLocaleString()}`;

type ImprovementPlanProps = {
  improvementPlan: ImprovementPlanItem[];
};

const ImprovementPlan: React.FC<ImprovementPlanProps> = ({ improvementPlan }) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const toggleExpand = (key: string) => {
    setExpandedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <Typography component="h2" variant="h6" sx={{ mt: 4, mb: 2 }}>
        Improvement Plan
      </Typography>
      {improvementPlan.map((plan, index) => {
        const totalCostLow = plan.CostBreakdown.reduce(
          (sum, breakdown) => sum + breakdown.LaborCostLow + breakdown.MaterialCostLow,
          0
        );
        const totalCostHigh = plan.CostBreakdown.reduce(
          (sum, breakdown) => sum + breakdown.LaborCostHigh + breakdown.MaterialCostHigh,
          0
        );

        return (
          <Accordion key={index} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography variant="h6" sx={{ mr: 2 }}>
                  {plan.Item}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mr: 4 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Cost: {formatCurrency(totalCostLow)} - {formatCurrency(totalCostHigh)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Value: {formatCurrency(plan.ValueIncreaseLow)} - {formatCurrency(plan.ValueIncreaseHigh)}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {plan.Description}
              </Typography>
              {plan.CostBreakdown.map((breakdown, rowIndex) => {
                const key = `${index}-${rowIndex}`;
                const totalWorkItemCostLow = breakdown.LaborCostLow + breakdown.MaterialCostLow;
                const totalWorkItemCostHigh = breakdown.LaborCostHigh + breakdown.MaterialCostHigh;

                return (
                  <Box key={key} sx={{ py: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold', mt: 0, mb: 0 }}
                      >
                        {breakdown.WorkItem}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'bold', color: '#555' }}
                        >
                          {formatCurrency(totalWorkItemCostLow)} - {formatCurrency(totalWorkItemCostHigh)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(key)}
                          sx={{ p: 0 }}
                        >
                          {expandedItems[key] ? <RemoveIcon /> : <AddIcon />}
                        </IconButton>
                      </Box>
                    </Box>
                    {expandedItems[key] && (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell></TableCell>
                            <TableCell><strong>Low</strong></TableCell>
                            <TableCell><strong>Medium</strong></TableCell>
                            <TableCell><strong>High</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell><strong>Labor</strong></TableCell>
                            <TableCell>{formatCurrency(breakdown.LaborCostLow)}</TableCell>
                            <TableCell>{formatCurrency(breakdown.LaborCostMedium)}</TableCell>
                            <TableCell>{formatCurrency(breakdown.LaborCostHigh)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Material</strong></TableCell>
                            <TableCell>{formatCurrency(breakdown.MaterialCostLow)}</TableCell>
                            <TableCell>{formatCurrency(breakdown.MaterialCostMedium)}</TableCell>
                            <TableCell>{formatCurrency(breakdown.MaterialCostHigh)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Total</strong></TableCell>
                            <TableCell>
                              {formatCurrency(breakdown.LaborCostLow + breakdown.MaterialCostLow)}
                            </TableCell>
                            <TableCell>
                              {formatCurrency(breakdown.LaborCostMedium + breakdown.MaterialCostMedium)}
                            </TableCell>
                            <TableCell>
                              {formatCurrency(breakdown.LaborCostHigh + breakdown.MaterialCostHigh)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    )}
                    <Divider sx={{ my: 2 }} />
                  </Box>
                );
              })}
              <Box sx={{ mt: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                  Projected Total Cost: {formatCurrency(totalCostLow)} - {formatCurrency(totalCostHigh)}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );
};

export default ImprovementPlan;
